import { useCallback, useRef, useState } from "react";
import styles from "./list.module.css";
import dayjs from "dayjs";
import { TextField, Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, Typography, Checkbox, Dialog, IconButton, InputAdornment } from "@mui/material";
import { Add, ClearRounded } from "@mui/icons-material";
import { Calendar, Inbox, Search } from "react-feather";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import TablePaginationComponent from "../../../components/TablePagination";
import { PersonAddAltOutlined } from "@mui/icons-material";
import Loader from "../../../components/Loader";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import { DateRangePicker } from "rsuite";
import { ComponentToPrintList } from "./ComponentToPrintList";
import { useReactToPrint } from "react-to-print";
import AddDriverForm from "../../../components/popups/AddDriverForm";
import AssignPopup from "../../../components/popups/AssignBookingDriverPopup";

const ButtonArea = ({ data }) => {
  const componentRef = useRef();
  const Export = ({ onExport }) => (<Button onClick={(e) => onExport(e.target.value)} className={`${styles.TableAddBU}`} style={{ width: "auto" }}>Export To CSV</Button>);

  const convertArrayOfObjectsToCSV = (array) => {
    let result = "";

    const columnDelimiter = ",";
    const lineDelimiter = "\n";
    const keys = ["booking_id", "post_code", "customer_name", "pickup_address", "order_status", "contact_no", "pickup_date"];

    keys.forEach((key) => {
      if (key === "booking_id") {
        result += "Booking Id";
      } else if (key === "post_code") {
        result += "Post Code";
      } else if (key === "customer_name") {
        result += "Customer Name";
      } else if (key === "pickup_address") {
        result += "Pickup _Address";
      } else if (key === "order_status") {
        result += "Order Status";
      } else if (key === "contact_no") {
        result += "Contact No";
      } else if (key === "pickup_date") {
        result += "Pickup Date";
      } else {
        result += key;
      }
      result += columnDelimiter;
    });
    result += lineDelimiter;

    array.forEach((item) => {
      let ctr = 0;
      keys.forEach((key) => {
        if (ctr > 0) result += columnDelimiter;
        result += item[key];
        ctr++;
      });
      result += lineDelimiter;
    });

    return result;
  };

  const downloadCSV = useCallback(() => {
    let newArray = data.map((row) => {
      return {
        booking_id: row?.booking_id,
        post_code: row?.post_code,
        customer_name: row?.customer_name,
        pickup_address: row?.pickup_details?.address.replace(/,/g, ""),
        order_status: row?.order_status === 1 || row?.payment_status === "1" ? "Paid" : "Unpaid",
        contact_no: row?.pickup_details?.phone,
        pickup_date: dayjs(row.pickup_date).format("DD/MM/YYYY"),
      };
    });

    const link = document.createElement("a");
    let csv = convertArrayOfObjectsToCSV(newArray);
    if (csv == null) return;

    const filename = "driver-list.csv";

    if (!csv.match(/^data:text\/csv/i)) {
      csv = `data:text/csv;charset=utf-8,${csv}`;
    }
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute("download", filename);
    link.click();
  }, [data]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  return (<div className={styles.ButtonAreaWrap}>
    <Export onExport={() => downloadCSV()} />
    <Button onClick={handlePrint} className={`${styles.TableAddBU}`} style={{ width: "auto" }}>Print Bookings</Button>
    <div style={{ display: "none" }}>
      <ComponentToPrintList ref={componentRef} data={data} />
    </div>
  </div>);
};

const BookingList = ({ selected, filteredData, filterVars, driverList, setSelected, setFilterVars, setAllData, setSnackbarOpen, setSnackbarMessage, setSnackbarType, page, setPage, rowsPerPage, setRowsPerPage, totalCount, onAssign }) => {
  const auth = authData();
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [driverFormOpen, setDriverFormOpen] = useState(false);

  const canAssign = auth?.user_type === 2 || auth?.permissions?.indexOf('bookings:assign-driver') > -1;

  const paginationChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginationChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const selectRowEvent = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const assignDrivers = () => {
    if (selected.length === 0) {
      setSnackbarType("error");
      setSnackbarOpen(true);
      setSnackbarMessage("Please select booking");
      return false;
    }
    setFormOpen(true);
  };


  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />

    <div className={styles.FilterBar}>
      <div className={styles.SearchBox}>
        <Search className={styles.SearchIcon} />
        <TextField
          fullWidth
          placeholder="Search booking ID, customer, postcode..."
          value={filterVars?.searchKey}
          onChange={(e) => setFilterVars((prev) => { return { ...prev, searchKey: e.target.value }; })}
          InputProps={{
            endAdornment: filterVars.searchKey && (<InputAdornment position="end">
              <IconButton size="small" aria-label="Clear search" onClick={(e) => { setFilterVars((prev) => { return { ...prev, searchKey: '' }; }); }}>
                <ClearRounded fontSize="small" />
              </IconButton>
            </InputAdornment>),
          }}
        />
      </div>

      <div className={styles.DateFilter}>
        <DateRangePicker
          format={"dd/MM/yyyy"}
          placement={"bottomEnd"}
          placeholder="Serach by date"
          caretAs={Calendar}
          value={filterVars?.date}
          showOneCalendar
          onChange={(e) => setFilterVars((prev) => { return { ...prev, date: e }; })}
        />
      </div>
    </div>

    <div className={`${styles.TableFilterHead}`}>
      <div className={`${styles.FilterCard}`}>
        <div className={`${styles.FilerButton}`}>
          {canAssign && <Button className={`${styles.TableAddBU}`} onClick={(e) => assignDrivers()}><PersonAddAltOutlined />Assign</Button>}
          {canAssign && <Button className={`${styles.TableAddBU}`} onClick={(e) => setDriverFormOpen(true)}><Add />Driver</Button>}
        </div>
      </div>
    </div>

    <div className={`${styles.FilterCardButton}`}>
      {selected.length > 0 ? (<Typography sx={{ flex: "1 1 100%" }} color="inherit" variant="subtitle1" component="div">{selected.length} item selected</Typography>) : (<ButtonArea style={{ float: "right" }} data={filteredData}></ButtonArea>)}
    </div>
    <div className={`${styles.CusTable}`}>
      <TableContainer component={Paper} className={styles.ResponsiveTableWrap}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {canAssign && <StyledMainTableCell padding="checkbox">
                <Checkbox
                  className={`${styles.AllCheckbox}`}
                  color="primary"
                  indeterminate={selected?.length > 0 && selected?.length < filteredData?.length}
                  checked={filteredData?.length > 0 && selected?.length === filteredData?.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newSelected = filteredData.map((n) => n.id);
                      setSelected(newSelected);
                      return;
                    }
                    setSelected([]);
                  }}
                />
              </StyledMainTableCell>}
              <StyledMainTableCell>Booking Id</StyledMainTableCell>
              <StyledMainTableCell>Post Code</StyledMainTableCell>
              <StyledMainTableCell>Customer Name</StyledMainTableCell>
              <StyledMainTableCell>Pickup Address</StyledMainTableCell>
              <StyledMainTableCell>Order Status</StyledMainTableCell>
              <StyledMainTableCell>Contact No</StyledMainTableCell>
              <StyledMainTableCell>Pickup Date</StyledMainTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData.map((item) => {
              const isItemSelected = selected.indexOf(item?.id) !== -1;

              return (<StyledMainTableRow key={item?.id} className={styles.ResponsiveRow} sx={{ "&:last-child td, &:last-child th": { border: 0, cursor: "pointer" } }} onClick={(e) => selectRowEvent(item?.id)} aria-checked={isItemSelected} selected={isItemSelected}>
                {canAssign && <TableCell padding="checkbox" className={styles.CheckboxCell}>
                  <Checkbox color="primary" checked={isItemSelected} />
                </TableCell>}
                <TableCell component="th" scope="row" data-label="Booking Id" className={styles.Mono}>{item?.booking_id}</TableCell>
                <TableCell component="th" scope="row" data-label="Post Code">{item?.post_code}</TableCell>
                <TableCell component="th" scope="row" data-label="Customer">{item?.customer_name}</TableCell>
                <TableCell component="th" scope="row" data-label="Pickup Address">{item?.pickup_details?.address}</TableCell>
                <TableCell data-label="Status">
                  {item?.payment_status === 1 || item?.payment_status === "1" ? (<Button className={`${styles.StatusBU} ${styles.DisaBU}`}>Paid</Button>) : (<Button className={`${styles.StatusBU}`}>Unpaid</Button>)}
                </TableCell>
                <TableCell component="th" scope="row" data-label="Contact No">{item?.pickup_details?.phone}</TableCell>
                <TableCell component="th" scope="row" data-label="Pickup Date">{dayjs(item?.pickup_date).format("DD/MM/YYYY")}</TableCell>
              </StyledMainTableRow>);
            })}

            {filteredData.length === 0 && (<TableRow>
              <TableCell colSpan={10} align="center">
                <div className={styles.EmptyState}>
                  <Inbox size={32} />
                  <p className={styles.EmptyStateTitle}>No bookings found</p>
                  <p className={styles.EmptyStateText}>Try a different date range or clear your search.</p>
                </div>
              </TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredData?.length > 0 && (<div className={styles.PaginationWrap}>
        <TablePaginationComponent
          count={totalCount}
          colSpan={6}
          page={page}
          rowsPerPage={rowsPerPage}
          handleChangePage={paginationChangePage}
          handleChangeRowsPerPage={paginationChangeRowsPerPage}
        />
      </div>)}
    </div>

    <Dialog open={formOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
      <AssignPopup setLoading={setLoading.bind(this)} modalClose={setFormOpen.bind(this)} bookingIds={selected} onSubmit={onAssign.bind(this)} setFormOpen={setFormOpen.bind(this)} />
    </Dialog>

    <Dialog open={driverFormOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
      <AddDriverForm setFormOpen={setDriverFormOpen.bind(this)} setAllData={null} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setLoading={setLoading.bind(this)} editData={null} />
    </Dialog>

  </>);
};

export default withAdminAuth(BookingList);